import type {
  ReadinessRaceState,
  SanityReadinessRaceConfig
} from '@lib/sanity/types';
import type { MiniLeagueSummary } from 'types/readiness';

/**
 * Tuning defaults for the post-GW1 race model (spec 6.3). Every value is
 * Sanity-editable via the quizPage `readinessRace` config; these fill in
 * for missing fields.
 */
export const DEFAULT_RACE_CONFIG: SanityReadinessRaceConfig = {
  behindMinGap: 4,
  aheadMinClear: 4,
  projectionGameweek: 6,
  behindMultiplier: 2.6,
  behindCap: 30,
  levelDrift: 12,
  titlePacePercent: 96,
  userMinPercent: 18,
  userMaxPercent: 88,
  gapToPercentScale: 4,
  minSeparationPercent: 4,
  ghostFloorPercent: 8,
  aheadGhostDropPercent: 14,
  paceMarkerLabel: 'WINNING PACE',
  paceStatusLabel: 'Tracking your race'
};

/**
 * Merges a Sanity race config over the defaults. The CMS document can be
 * partially filled (missing or null fields), so only finite numbers and
 * non-empty strings override.
 */
export const resolveRaceConfig = (
  config: Partial<SanityReadinessRaceConfig> | null | undefined
): SanityReadinessRaceConfig => {
  if (!config) return DEFAULT_RACE_CONFIG;

  const merged = { ...DEFAULT_RACE_CONFIG };
  for (const key of Object.keys(
    DEFAULT_RACE_CONFIG
  ) as (keyof SanityReadinessRaceConfig)[]) {
    const value = config[key];
    const fallback = merged[key];

    if (typeof fallback === 'number') {
      if (typeof value === 'number' && Number.isFinite(value)) {
        (merged[key] as number) = value;
      }
    } else if (typeof value === 'string' && value.trim() !== '') {
      (merged[key] as string) = value;
    }
  }

  return merged;
};

export type RaceMetrics = {
  state: Exclude<ReadinessRaceState, 'lateStart'>;
  /**
   * Real FPL points: behind/level → points behind the leader (>= 0),
   * ahead → points clear of second place.
   */
  gap: number;
};

/**
 * Derives the race state from real standings (spec 6.3 state model).
 * The two thresholds define the whole model: non-leaders behindMinGap+
 * behind are "behind"; leaders aheadMinClear+ over second place are
 * "ahead"; everything between the thresholds is "level".
 */
export const deriveRaceMetrics = (
  league: MiniLeagueSummary,
  config: SanityReadinessRaceConfig = DEFAULT_RACE_CONFIG
): RaceMetrics => {
  if (league.userRank === 1) {
    const clear = Math.max(
      0,
      league.userPoints - (league.secondPoints ?? league.userPoints)
    );
    return clear >= config.aheadMinClear
      ? { state: 'ahead', gap: clear }
      : {
          state: 'level',
          gap: Math.max(0, league.leaderPoints - league.userPoints)
        };
  }

  const gap = Math.max(0, league.leaderPoints - league.userPoints);
  return gap >= config.behindMinGap
    ? { state: 'behind', gap }
    : { state: 'level', gap };
};

/**
 * The held-back projection (spec 6.3): behind compounds deterministically,
 * level uses a fixed drift figure, ahead and lateStart carry no number
 * (erosion framing only — never fabricate points).
 */
export const projectGap = (
  state: ReadinessRaceState,
  gap: number,
  config: SanityReadinessRaceConfig = DEFAULT_RACE_CONFIG
): number | null => {
  switch (state) {
    case 'behind':
      return Math.min(
        Math.round(gap * config.behindMultiplier),
        config.behindCap
      );
    case 'level':
      return config.levelDrift;
    default:
      return null;
  }
};
